/**
 * UV-transform math for the ground's three tiling layers (base texture,
 * secondary texture, noise mask) — consumed by `scene.ts`, kept pure and
 * browser-free so it can be unit-tested in Node.
 *
 * Deliberately NOT three's own texture transform (`Texture.repeat` +
 * `Texture.rotation`, i.e. `Matrix3.setUvTransform`): that rotates first and
 * then applies a *per-axis* scale of `repeat`. With `repeat = (width * tpu,
 * depth * tpu)` — the only way to express "tiles per metre" through that API —
 * any non-zero rotation on non-square ground shears the tiles into
 * parallelograms whose shape changes with the angle.
 *
 * Mapping UV -> metres -> rotate -> tiles instead makes the world->tile
 * transform a pure rotation plus a *uniform* scale, so `tilesPerUnit` means
 * literally "tiles per metre" at every angle and every ground aspect ratio.
 */

/** A 3x3 matrix in column-major order — feed straight to `THREE.Matrix3.fromArray()`. */
export type Mat3Elements = [number, number, number, number, number, number, number, number, number];

/**
 * Build the UV transform for one ground layer.
 *
 * @param worldWidth  Ground plane width in metres (X).
 * @param worldDepth  Ground plane depth in metres (Z).
 * @param tilesPerUnit Tiles per metre — higher = smaller, more frequent tiles.
 * @param rotation    Layer rotation in radians, about the centre of the ground.
 */
export function groundUvMatrix(
  worldWidth: number,
  worldDepth: number,
  tilesPerUnit: number,
  rotation: number,
): Mat3Elements {
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  // Metres-per-UV on each axis, pre-multiplied by the tile density.
  const sx = tilesPerUnit * worldWidth;
  const sy = tilesPerUnit * worldDepth;
  // Rotation happens about UV (0.5, 0.5) — the middle of the plane — so
  // spinning a layer pivots the pattern in place instead of sliding it away.
  return [
    cos * sx, sin * sx, 0,
    -sin * sy, cos * sy, 0,
    -0.5 * (cos * sx - sin * sy), -0.5 * (sin * sx + cos * sy), 1,
  ];
}

/** Apply a `Mat3Elements` to a UV point — the JS twin of `(m * vec3(uv, 1.0)).xy` in GLSL. */
export function applyUvMatrix(m: Mat3Elements, u: number, v: number): { u: number; v: number } {
  return {
    u: m[0] * u + m[3] * v + m[6],
    v: m[1] * u + m[4] * v + m[7],
  };
}
